import { toError } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { identifyUser } from "@src/appV2/FeatureFlags/identifyUser";
import { APP_V2_USER_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useGetHasOpenPlacementsNearby } from "@src/appV2/redesign/PlacementCandidate/api/useGetHasOpenPlacementsNearby";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";
import { useWorkerDocumentsByStatus } from "@src/appV2/Worker/useWorkerDocumentsByStatus";
import { useQuery } from "@tanstack/react-query";
// eslint-disable-next-line no-restricted-imports
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useCallback } from "react";

interface UseAskInterestedWorkTypeParams {
  workerId: string;
}

export function useAskInterestedWorkType(params: UseAskInterestedWorkTypeParams) {
  const { workerId } = params;
  const { refetch: refetchWorker } = useGetWorker({
    enabled: false,
  });

  const { refetch: refetchHasOpenPlacements } = useGetHasOpenPlacementsNearby(
    {
      workerId,
    },
    {
      enabled: false,
    }
  );

  const ldClient = useLDClient();

  // This is required to identify the user in LD
  const documentsByStatus = useWorkerDocumentsByStatus();

  const shouldAskInterestedWorkType = useCallback(async () => {
    try {
      if (!workerId) {
        return false;
      }

      const latestWorkerResponse = await refetchWorker({
        throwOnError: true,
      });
      const updatedWorker = latestWorkerResponse.data;

      // We ask for interested work type after we ask for address
      // We rely on MSA targeted flags to determine if the user is eligible for the feature flag
      // But since user just updated their address, the MSA may not yet updated in LD due to race condition
      // Re-identify the user so that the worker's MSA is updated in LD
      if (ldClient) {
        await identifyUser({
          inputForContext: {
            worker: updatedWorker,
            documentsByStatus,
          },
          client: ldClient,
        });
      }

      // Evaluate the surface jobs flag explicitly by directly calling the launch darkly client
      // This is necessary because at this point the value returned by the useCbhFlag might be outdated as we have just updated the LD user's MSA
      const isSurfaceJobsEnabled = Boolean(ldClient?.variation(CbhFeatureFlag.SURFACE_JOBS, false));
      const hasOpenPlacementsNearby = await refetchHasOpenPlacements({
        throwOnError: true,
      });
      return Boolean(isSurfaceJobsEnabled && hasOpenPlacementsNearby.data);
    } catch (error: unknown) {
      logError(APP_V2_USER_EVENTS.ASK_INTERESTED_WORK_TYPE_ERROR, {
        error: toError(error),
        metadata: {
          title: "Error asking interested work type",
        },
      });

      // This screen is not critical, so we can continue the flow even if we fail to fetch the data
      return false;
    }
  }, [workerId, refetchWorker, ldClient, refetchHasOpenPlacements, documentsByStatus]);

  return useQuery({
    queryKey: ["should-ask-interested-work-type", workerId],
    queryFn: shouldAskInterestedWorkType,
    meta: {
      logErrorMessage: APP_V2_USER_EVENTS.ASK_INTERESTED_WORK_TYPE_ERROR,
    },
  });
}
